import { useState, useEffect } from 'react';
import { Download, ChevronUp, Loader2, CreditCard, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Parameter } from '@shared/types';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { downloadSTLFile, downloadOpenSCADFile } from '@/utils/downloadUtils';
import { useBlob } from '@/contexts/BlobContext';
import { PrintabilityStatus } from '@/components/parameter/PrintabilityStatus';
import { m, AnimatePresence } from 'framer-motion';
import { createCheckoutSession } from '@/services/purchases';
import { useDownloadAccess } from '@/hooks/useDownloadAccess';
import { useHighQualityExport } from '@/hooks/useHighQualityExport';
import { type WorkerFile } from '@/hooks/useOpenSCAD';
import {
  type ExportQuality,
  EXPORT_QUALITY_LABELS,
  EXPORT_QUALITY_DESCRIPTIONS,
} from '@/utils/exportQualityUtils';

export interface DownloadButtonProps {
  scadCode?: string;
  className?: string;
  /** Download price in dollars (e.g. 0.50). Null/undefined = free. */
  price?: number | null;
  parameters?: Parameter[];
  /** The model's database ID (required for paid downloads). */
  modelId?: string | null;
  /** The current authenticated user's ID (null for anonymous). */
  userId?: string | null;
  /** Project files to sync to the export worker for high-quality compiles. */
  projectFiles?: WorkerFile[];
}

const QUALITY_OPTIONS: ExportQuality[] = [1, 2, 4, 8];
const EMPTY_PROJECT_FILES: WorkerFile[] = [];

export function DownloadButton({
  scadCode,
  className,
  price,
  parameters,
  modelId,
  userId,
  projectFiles = EMPTY_PROJECT_FILES,
}: DownloadButtonProps) {
  const { blob, coloredBlobs } = useBlob();
  const [selectedFormat, setSelectedFormat] = useState<'stl' | 'scad'>('stl');
  const [exportQuality, setExportQuality] = useState<ExportQuality>(1);
  const [isReady, setIsReady] = useState(false);
  const [isCheckingOut, setIsCheckingOut] = useState(false);

  const isPaid = price != null && price > 0;

  // Only check for existing purchases on logged-in users
  const { hasPurchased, isLoading: isPurchaseLoading } = useDownloadAccess(
    isPaid ? modelId : null,
    isPaid ? userId : null,
  );

  const { compileForExport, isExporting, exportProgress } =
    useHighQualityExport();

  const isDownloadDisabled = selectedFormat === 'stl' ? !blob : !scadCode;
  // For paid models, also disable while checking purchase status
  const isButtonDisabled =
    isDownloadDisabled ||
    (isPaid && isPurchaseLoading) ||
    isCheckingOut ||
    isExporting;

  // Trigger animation when the download becomes enabled
  useEffect(() => {
    if (!isDownloadDisabled) {
      setIsReady(true);
      const timer = setTimeout(() => setIsReady(false), 1000);
      return () => clearTimeout(timer);
    } else {
      setIsReady(false);
    }
  }, [isDownloadDisabled]);

  const performDownload = async () => {
    if (selectedFormat === 'stl') {
      if (!blob) return;

      // At 1x quality, use the already-compiled blob from BlobContext
      if (exportQuality === 1) {
        await downloadSTLFile(blob, undefined, coloredBlobs, parameters);
        return;
      }

      // Higher quality: re-compile with scaled $fn
      const result = await compileForExport(
        scadCode ?? '',
        projectFiles,
        exportQuality,
      );
      if (result) {
        await downloadSTLFile(
          result.blob,
          undefined,
          result.coloredBlobs,
          parameters,
        );
      }
    } else {
      if (!scadCode) return;
      downloadOpenSCADFile(scadCode, undefined);
    }
  };

  const handleDownload = async () => {
    // Free model or already purchased — download directly
    if (!isPaid || hasPurchased) {
      await performDownload();
      return;
    }

    // Paid model, not yet purchased — redirect to Stripe Checkout
    if (!modelId) {
      console.error('DownloadButton: modelId is required for paid downloads');
      return;
    }

    setIsCheckingOut(true);
    try {
      const currentUrl = window.location.href.split('?')[0]; // strip existing params
      const result = await createCheckoutSession({
        modelId,
        userId: userId ?? null,
        successUrl: currentUrl,
        cancelUrl: currentUrl,
      });

      if (result.already_purchased) {
        // Edge Function confirmed the purchase — download now
        await performDownload();
        return;
      }

      if (result.checkout_url) {
        // Redirect to Stripe Checkout
        window.location.href = result.checkout_url;
      }
    } catch (err) {
      console.error('Checkout session error:', err);
    } finally {
      setIsCheckingOut(false);
    }
  };

  // Determine label and icon state
  const getButtonContent = () => {
    if (isCheckingOut) {
      return (
        <span className="flex items-center">
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          Redirecting...
        </span>
      );
    }

    if (isExporting) {
      const msg = exportProgress?.message ?? 'Rendering...';
      return (
        <span className="flex items-center">
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          {msg}
        </span>
      );
    }

    if (isPurchaseLoading && isPaid) {
      return (
        <span className="flex items-center">
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          {selectedFormat.toUpperCase()}
        </span>
      );
    }

    const showPayIcon = isPaid && !hasPurchased;
    const qualityLabel =
      selectedFormat === 'stl' && exportQuality > 1
        ? ` · ${EXPORT_QUALITY_LABELS[exportQuality]}`
        : '';

    return (
      <m.div
        className="flex items-center"
        animate={isReady ? { y: [0, -4, 0] } : {}}
        transition={{ duration: 0.4, ease: 'easeOut' }}
      >
        {showPayIcon ? (
          <CreditCard className="mr-2 h-4 w-4" />
        ) : (
          <Download className="mr-2 h-4 w-4" />
        )}
        {selectedFormat.toUpperCase()}
        {qualityLabel}
        {showPayIcon && price != null
          ? ` · $${price.toFixed(2)}`
          : hasPurchased && isPaid
            ? ' · Purchased'
            : null}
      </m.div>
    );
  };

  return (
    <div className={className}>
      <div className="mb-3">
        <PrintabilityStatus />
      </div>
      <div className="flex">
        <Button
          onClick={handleDownload}
          disabled={isButtonDisabled}
          aria-label={`download ${selectedFormat.toUpperCase()} file`}
          className="relative h-10 flex-1 overflow-hidden rounded-r-none"
        >
          {getButtonContent()}
          {/* Subtle shine effect when becoming ready */}
          <AnimatePresence>
            {isReady && !isPaid && (
              <m.div
                initial={{ x: '-100%', opacity: 0 }}
                animate={{ x: '100%', opacity: 0.3 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.8, ease: 'easeInOut' }}
                className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent"
              />
            )}
          </AnimatePresence>
        </Button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              disabled={!blob && !scadCode}
              aria-label="select download format"
              className="h-10 w-10 rounded-l-none border-l border-primary-foreground/20 p-0"
            >
              <ChevronUp className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-52">
            {QUALITY_OPTIONS.map((q) => (
              <DropdownMenuItem
                key={q}
                onClick={() => {
                  setSelectedFormat('stl');
                  setExportQuality(q);
                }}
                disabled={!blob}
                className="cursor-pointer"
              >
                <span className="text-sm">.STL {EXPORT_QUALITY_LABELS[q]}</span>
                <span className="ml-auto flex items-center gap-1.5 text-xs text-muted-foreground">
                  {EXPORT_QUALITY_DESCRIPTIONS[q]}
                  {selectedFormat === 'stl' && exportQuality === q && (
                    <Check className="h-3 w-3" />
                  )}
                </span>
              </DropdownMenuItem>
            ))}
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() => setSelectedFormat('scad')}
              disabled={!scadCode}
              className="cursor-pointer"
            >
              <span className="text-sm">.SCAD</span>
              <span className="ml-auto flex items-center gap-1.5 text-xs text-muted-foreground">
                OpenSCAD Code
                {selectedFormat === 'scad' && <Check className="h-3 w-3" />}
              </span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
}
